import React, { MouseEvent, KeyboardEvent } from 'react'
import {
  ChipButton,
  InlineButton,
  InlineSize,
  FormButton,
  PrimaryButton,
  SecondaryButton,
  Spacer,
  Spinner,
  TertiaryButton,
  Tooltip,
  TableButton,
} from './Button.styled'
import { Text } from '../Text'
import { colors } from '../../tokens/colors'
import { Button as MUIButton, ButtonProps as MUIButtonProps } from './MUIButton'

interface CommonButtonProps {
  tooltip?: string
  isLoading?: boolean
  isDisabled?: boolean
  type?: 'submit' | 'button' | 'reset'
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void
}

interface ComplexButtonProps {
  label: string
  flush?: 'top' | 'bottom' | 'left' | 'right'
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
}

export interface ChipButtonProps extends CommonButtonProps, ComplexButtonProps {
  variant: 'chip'
}

export interface InlineButtonProps extends CommonButtonProps {
  variant: 'inline'
  size: InlineSize
  icon: React.ReactNode
}

export interface PrimaryButtonProps
  extends CommonButtonProps,
    ComplexButtonProps {
  variant: 'primary'
}

export interface SecondaryButtonProps
  extends CommonButtonProps,
    ComplexButtonProps {
  variant: 'secondary'
}

export interface TertiaryButtonProps
  extends CommonButtonProps,
    ComplexButtonProps {
  variant: 'tertiary'
}

export interface FormButtonProps extends CommonButtonProps, ComplexButtonProps {
  variant: 'form'
}

export interface TableButtonProps
  extends CommonButtonProps,
    ComplexButtonProps {
  variant: 'table'
}

type ButtonProps =
  | ChipButtonProps
  | InlineButtonProps
  | PrimaryButtonProps
  | SecondaryButtonProps
  | TertiaryButtonProps
  | FormButtonProps
  | TableButtonProps

const BSButton = ({
  variant,
  tooltip,
  onClick,
  onKeyDown,
  type = 'button',
  isLoading = false,
  isDisabled = false,
  ...rest
}: ButtonProps) => {
  const handleKeyPress = (event: KeyboardEvent<HTMLButtonElement>) => {
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      event.preventDefault()
      onClick(event as unknown as MouseEvent<HTMLButtonElement>)
    }
    if (onKeyDown) onKeyDown(event)
  }

  const commonProps = {
    type,
    variant,
    onClick,
    isLoading,
    tabIndex: 0,
    disabled: isDisabled,
    onKeyDown: handleKeyPress,
  }

  let button = null

  const { label, leadingIcon, trailingIcon, flush } = rest as ChipButtonProps

  switch (variant) {
    case 'chip':
      button = (
        <ChipButton aria-label={label} flush={flush} {...commonProps}>
          {leadingIcon}
          {label && <Text variant='p2'>{label}</Text>}
          {isLoading ? <Spinner /> : trailingIcon}
          {tooltip && <Tooltip>{tooltip}</Tooltip>}
        </ChipButton>
      )
      break
    case 'inline':
      const { size, icon } = rest as InlineButtonProps

      button = (
        <InlineButton aria-label={tooltip} size={size} {...commonProps}>
          {isLoading ? <Spinner /> : icon}
          {tooltip && <Tooltip>{tooltip}</Tooltip>}
        </InlineButton>
      )
      break
    case 'primary':
      button = (
        <PrimaryButton aria-label={label} flush={flush} {...commonProps}>
          {leadingIcon}
          {label && (
            <Spacer
              hasLeadingElement={!!leadingIcon}
              hasTrailingElement={isLoading || !!trailingIcon}
            >
              <Text variant='p1'>{label}</Text>
            </Spacer>
          )}
          {isLoading ? (
            <Spinner borderSize={2} color={colors.copy.paragraphLight} />
          ) : (
            trailingIcon
          )}
        </PrimaryButton>
      )
      break
    case 'secondary':
      button = (
        <SecondaryButton aria-label={label} flush={flush} {...commonProps}>
          {leadingIcon}
          {label && (
            <Spacer
              hasLeadingElement={!!leadingIcon}
              hasTrailingElement={isLoading || !!trailingIcon}
            >
              <Text variant='p1'>{label}</Text>
            </Spacer>
          )}
          {isLoading ? <Spinner borderSize={2} /> : trailingIcon}
        </SecondaryButton>
      )
      break
    case 'tertiary':
      button = (
        <TertiaryButton aria-label={label} flush={flush} {...commonProps}>
          {leadingIcon}
          {label && (
            <Spacer
              hasLeadingElement={!!leadingIcon}
              hasTrailingElement={isLoading || !!trailingIcon}
            >
              <Text variant='p1'>{label}</Text>
            </Spacer>
          )}
          {isLoading ? <Spinner borderSize={2} /> : trailingIcon}
        </TertiaryButton>
      )
      break
    case 'form':
      button = (
        <FormButton aria-label={label} flush={flush} {...commonProps}>
          {leadingIcon}
          {label && (
            <Spacer
              hasLeadingElement={!!leadingIcon}
              hasTrailingElement={isLoading || !!trailingIcon}
            >
              <Text variant='p2'>{label}</Text>
            </Spacer>
          )}
          {isLoading ? <Spinner borderSize={2} /> : trailingIcon}
          {tooltip && <Tooltip>{tooltip}</Tooltip>}
        </FormButton>
      )
      break
    case 'table':
      button = (
        <TableButton aria-label={label} flush={flush} {...commonProps}>
          {leadingIcon}
          {label && (
            <Spacer
              hasLeadingElement={!!leadingIcon}
              hasTrailingElement={isLoading || !!trailingIcon}
            >
              <Text variant='p2'>{label}</Text>
            </Spacer>
          )}
          {isLoading ? <Spinner borderSize={2} /> : trailingIcon}
          {tooltip && <Tooltip>{tooltip}</Tooltip>}
        </TableButton>
      )
      break
    default:
      button = null
      break
  }

  return button
}

const BSVariants = [
  'chip',
  'inline',
  'primary',
  'secondary',
  'tertiary',
  'form',
  'table',
]
export const Button = (props: ButtonProps | MUIButtonProps) =>
  !props.variant || BSVariants.includes(props.variant) ? (
    <BSButton {...(props as ButtonProps)} />
  ) : (
    <MUIButton {...(props as MUIButtonProps)} />
  )
